import { useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  getSession,
  login as apiLogin,
  register as apiRegister,
  type LoginParams,
  type RegisterParams,
} from '@/api/auth'
import type { UserView } from '@/api/types'
import { useAuthStore, type AuthUser } from './authStore'

/** Map the server's user view (id) to the stored session shape (agentId). */
function toAuthUser(src: { user: UserView }): AuthUser {
  return {
    agentId: src.user.id,
    agencyId: src.user.agencyId,
    email: src.user.email,
    fullName: src.user.fullName,
    role: src.user.role,
    emailSignature: src.user.emailSignature ?? null,
  }
}

/**
 * Keeps the stored identity in sync with the server: on mount (and on window refocus) fetches
 * GET /api/auth/me and updates name / role / workspace branding in place, so a change made elsewhere
 * shows up without waiting for the next sign-in. Tokens and cached data are untouched.
 */
export function useSyncSession() {
  const isAuth = useAuthStore((s) => s.isAuthenticated)
  const refreshIdentity = useAuthStore((s) => s.refreshIdentity)
  const { data } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: getSession,
    enabled: isAuth,
    staleTime: 5 * 60 * 1000,
  })
  useEffect(() => {
    if (data) refreshIdentity(toAuthUser(data), data.agency)
  }, [data, refreshIdentity])
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession)
  return useMutation({
    mutationFn: (params: LoginParams) => apiLogin(params),
    onSuccess: (resp) => setSession(toAuthUser(resp), resp.agency, resp.accessToken, resp.refreshToken),
  })
}

export function useRegister() {
  const setSession = useAuthStore((s) => s.setSession)
  return useMutation({
    mutationFn: (params: RegisterParams) => apiRegister(params),
    onSuccess: (resp) => {
      // Verification enabled: no tokens — the component prompts the user to check their inbox.
      // Verification disabled (default): auto-login from the returned tokens, as before.
      if (resp.auth) {
        const { auth } = resp
        setSession(toAuthUser(auth), auth.agency, auth.accessToken, auth.refreshToken)
      }
    },
  })
}
